/**
 * CuadrillaContratista.jsx · v101.205.0 · Contratista / dueño y cuadrilla en el perfil del técnico líder.
 * Guarda por su cuenta (botón propio). Al guardar, Prefacturación factura las OTs del técnico a ese contratista.
 * También exporta CuadrillasImportar: importar el Excel «Contratistas y técnicos asignados» con vista previa.
 */
import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import BodyPortal from "./ui/BodyPortal";
import { Loader2, Save, Upload, X, CheckCircle2, AlertTriangle } from "lucide-react";

const VACIO = {
  sucursal: "", contratista_nombre: "", contratista_ci: "", contratista_telefono: "", contratista_correo: "",
  cuadrilla: "", tecnico_ci: "", tecnico_telefono: "", tecnico_correo: "", ayudante_nombre: "", ayudante_ci: "",
  ayudante_correo: "", vehiculo: "", spoke: "", estado: "Activo", lugar: "",
};
const VEHICULOS = ["Furgoneta", "Camioneta", "Motorizado", "Automóvil"];
const errMsg = (e, d) => (typeof e?.response?.data?.detail === "string" ? e.response.data.detail : d);

function In({ label, value, onChange, list, type = "text", placeholder, testId }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-semibold text-gray-600 mb-1 uppercase tracking-wider">{label}</span>
      <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} list={list}
        placeholder={placeholder} className="w-full input text-sm" data-testid={testId} />
    </label>
  );
}

export default function CuadrillaContratista({ userId, testIdPrefix = "cuadrilla" }) {
  const [f, setF] = useState(VACIO);
  const [meta, setMeta] = useState(null);
  const [duenos, setDuenos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [denegado, setDenegado] = useState(false);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    Promise.all([
      api.get(`/v1/cuadrillas/usuario/${userId}`),
      api.get("/v1/cuadrillas/contratistas").catch(() => ({ data: { items: [] } })),
    ]).then(([a, b]) => {
      const d = a.data || {};
      setF(Object.fromEntries(Object.keys(VACIO).map((k) => [k, d[k] ?? (k === "estado" ? "Activo" : "")])));
      setMeta({ pf: d.contratista_prefactura, by: d.updated_by, at: d.updated_at });
      setDuenos(b.data?.items || []);
    }).catch((e) => { if (e?.response?.status === 403) setDenegado(true); })
      .finally(() => setLoading(false));
  }, [userId]);

  // Al elegir un dueño ya registrado se completan sus datos
  const elegirDueno = (v) => {
    const d = duenos.find((x) => (x.contratista_nombre || "").toUpperCase() === String(v).toUpperCase());
    setF((x) => ({
      ...x, contratista_nombre: v,
      ...(d ? { contratista_ci: d.contratista_ci || x.contratista_ci, contratista_telefono: d.contratista_telefono || x.contratista_telefono,
                contratista_correo: d.contratista_correo || x.contratista_correo, sucursal: x.sucursal || d.sucursal || "" } : {}),
    }));
  };

  const guardar = async () => {
    if (!f.contratista_nombre.trim()) { toast.error("Indique el contratista / dueño"); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/v1/cuadrillas/usuario/${userId}`, f);
      setMeta({ pf: data.contratista_prefactura, by: "usted", at: new Date().toISOString() });
      toast.success(data.contratista_nuevo
        ? `Guardado · contratista nuevo en Prefacturación: ${data.contratista_prefactura}`
        : `Guardado · factura a ${data.contratista_prefactura}`);
    } catch (e) { toast.error(errMsg(e, "No se pudo guardar")); }
    finally { setSaving(false); }
  };

  if (!userId || denegado) return null;
  if (loading) return <div className="text-sm text-gray-500 flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Cargando…</div>;
  const p = testIdPrefix;
  return (
    <div className="space-y-3 rounded-lg border border-gray-200 p-3 bg-gray-50" data-testid={`${p}-box`}>
      <datalist id={`${p}-duenos`}>{duenos.map((d) => <option key={d.contratista_nombre} value={d.contratista_nombre} />)}</datalist>
      <datalist id={`${p}-vehiculos`}>{VEHICULOS.map((v) => <option key={v} value={v} />)}</datalist>
      <div className="text-[11px] font-bold text-[#0A3747] uppercase tracking-wider">Contratista / dueño</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <In label="Nombre del contratista / dueño *" value={f.contratista_nombre} onChange={elegirDueno} list={`${p}-duenos`} testId={`${p}-dueno`} />
        <In label="CI / RUC contratista" value={f.contratista_ci} onChange={set("contratista_ci")} />
        <In label="Teléfono contratista" value={f.contratista_telefono} onChange={set("contratista_telefono")} />
        <In label="Correo contratista" type="email" value={f.contratista_correo} onChange={set("contratista_correo")} />
        <In label="Sucursal" value={f.sucursal} onChange={set("sucursal")} />
        <In label="Spoke" value={f.spoke} onChange={set("spoke")} placeholder="Manabí, Los Ríos…" />
      </div>
      <div className="text-[11px] font-bold text-[#0A3747] uppercase tracking-wider pt-1">Cuadrilla</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <In label="N.º cuadrilla" value={f.cuadrilla} onChange={set("cuadrilla")} />
        <label className="block">
          <span className="block text-[10px] font-semibold text-gray-600 mb-1 uppercase tracking-wider">Estado cuadrilla</span>
          <select value={f.estado || "Activo"} onChange={(e) => set("estado")(e.target.value)} className="w-full input text-sm">
            <option>Activo</option><option>Inactivo</option>
          </select>
        </label>
        <In label="CI técnico líder" value={f.tecnico_ci} onChange={set("tecnico_ci")} />
        <In label="Teléfono técnico líder" value={f.tecnico_telefono} onChange={set("tecnico_telefono")} />
        <In label="Correo técnico líder (Excel)" type="email" value={f.tecnico_correo} onChange={set("tecnico_correo")} />
        <In label="Vehículo" value={f.vehiculo} onChange={set("vehiculo")} list={`${p}-vehiculos`} />
        <In label="Ayudante" value={f.ayudante_nombre} onChange={set("ayudante_nombre")} />
        <In label="CI ayudante" value={f.ayudante_ci} onChange={set("ayudante_ci")} />
        <In label="Correo ayudante" type="email" value={f.ayudante_correo} onChange={set("ayudante_correo")} />
        <In label="Lugar / evento" value={f.lugar} onChange={set("lugar")} />
      </div>
      <div className="flex items-center justify-between gap-2 pt-1 flex-wrap">
        <div className="text-[11px] text-gray-600">
          {meta?.pf ? <>Prefacturación: factura a <b>{meta.pf}</b></> : "Aún sin contratista enlazado en Prefacturación"}
        </div>
        <button type="button" onClick={guardar} disabled={saving}
          className="px-3 py-1.5 bg-[#0A3747] text-white text-xs rounded inline-flex items-center gap-1.5 disabled:opacity-40"
          data-testid={`${p}-guardar`}>
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Guardar contratista
        </button>
      </div>
    </div>
  );
}

const ESTADO_TXT = { enlazar: "Se enlaza", sin_usuario: "Sin usuario en AlfaField", repetido: "Repetido" };

export function CuadrillasImportar({ onDone, disabled }) {
  const [file, setFile] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const enviar = async (aplicar, fl = file) => {
    if (!fl) return;
    const fd = new FormData();
    fd.append("file", fl);
    setBusy(true);
    try {
      const { data } = await api.post(`/v1/cuadrillas/importar?aplicar=${aplicar ? "true" : "false"}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      setRes(data);
      if (aplicar) { toast.success(`${data.aplicadas} técnicos enlazados con su contratista`); onDone && onDone(); }
    } catch (e) { toast.error(errMsg(e, "No se pudo leer el Excel")); }
    finally { setBusy(false); }
  };
  const cerrar = () => { setFile(null); setRes(null); };
  return (
    <>
      <label className={`neu-btn cursor-pointer ${disabled ? "opacity-40 cursor-not-allowed" : ""}`}
        title="Excel «Contratistas y técnicos asignados»: enlaza cada técnico líder con su contratista / dueño (vista previa)"
        data-testid="cuadrillas-import-btn">
        <Upload size={14} /> Contratistas y cuadrillas
        <input type="file" accept=".xlsx" className="hidden" disabled={disabled}
          onChange={(e) => { const fl = e.target.files?.[0]; e.target.value = ""; if (fl) { setFile(fl); enviar(false, fl); } }} />
      </label>
      {file && (
        <BodyPortal>
          <div style={{ position: "fixed", inset: 0, zIndex: 5000, background: "rgba(15,23,42,.45)", display: "flex",
            alignItems: "center", justifyContent: "center", padding: 16 }} onClick={cerrar}>
            <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-xl shadow-xl w-full max-w-5xl flex flex-col"
              style={{ maxHeight: "calc(100vh - 32px)" }} data-testid="cuadrillas-import-modal">
              <div className="flex items-center justify-between px-5 py-3 border-b">
                <div className="font-bold text-[#0A3747]">Contratistas y cuadrillas · {res?.aplicado ? "resultado" : "vista previa"}</div>
                <button onClick={cerrar} className="p-1"><X size={16} /></button>
              </div>
              {busy && !res ? (
                <div className="p-8 flex justify-center"><Loader2 className="animate-spin" /></div>
              ) : res && (
                <>
                  <div className="px-5 py-3 text-sm flex flex-wrap gap-x-5 gap-y-1">
                    <span>Filas: <b>{res.total}</b></span>
                    <span className="text-green-700">Se enlazan: <b>{res.enlazables}</b></span>
                    <span className="text-amber-700">Sin usuario en AlfaField: <b>{res.sin_usuario}</b></span>
                    {res.repetidas > 0 && <span className="text-red-700">Repetidas: <b>{res.repetidas}</b></span>}
                    {res.contratistas_nuevos?.length > 0 && (
                      <span>Contratistas nuevos en Prefacturación: <b>{res.contratistas_nuevos.join(" · ")}</b></span>
                    )}
                  </div>
                  <div className="overflow-auto px-5" style={{ flex: 1 }}>
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-white">
                        <tr className="text-left text-gray-500 border-b">
                          <th className="py-1.5 pr-2">Fila</th><th className="pr-2">Técnico líder (Excel)</th>
                          <th className="pr-2">Usuario AlfaField</th><th className="pr-2">Por</th>
                          <th className="pr-2">Contratista → Prefacturación</th><th className="pr-2">Cuadrilla</th><th>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {res.items.map((x) => (
                          <tr key={x.fila} className="border-b align-top">
                            <td className="py-1.5 pr-2">{x.fila}</td>
                            <td className="pr-2">{x.tecnico}<div className="text-gray-400">{x.sucursal}</div></td>
                            <td className="pr-2">{x.usuario || "—"}{x.usuario_correo && <div className="text-gray-400">{x.usuario_correo}</div>}</td>
                            <td className="pr-2">{x.via || "—"}</td>
                            <td className="pr-2">{x.contratista_prefactura}{x.contratista_nuevo && <span className="ml-1 px-1 rounded bg-amber-100 text-amber-800">nuevo</span>}</td>
                            <td className="pr-2">{x.cuadrilla || "—"}</td>
                            <td>
                              <span className={x.estado === "enlazar" ? "text-green-700" : x.estado === "repetido" ? "text-red-700" : "text-amber-700"}>
                                {x.estado === "enlazar" ? <CheckCircle2 size={12} className="inline mr-1" /> : <AlertTriangle size={12} className="inline mr-1" />}
                                {res.aplicado && x.estado === "enlazar" ? "Enlazado" : ESTADO_TXT[x.estado] || x.estado}
                              </span>
                              {x.nota && <div className="text-gray-500">{x.nota}</div>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="px-5 py-3 border-t flex items-center justify-between gap-2 flex-wrap">
                    <div className="text-[11px] text-gray-500">
                      Las filas «Sin usuario» se enlazan a mano en el perfil del técnico (Editar → Contratista / dueño).
                    </div>
                    {!res.aplicado ? (
                      <button onClick={() => enviar(true)} disabled={busy || !res.enlazables}
                        className="px-4 py-2 bg-[#0A3747] text-white text-sm rounded inline-flex items-center gap-2 disabled:opacity-40"
                        data-testid="cuadrillas-import-aplicar">
                        {busy ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                        Aplicar {res.enlazables} enlaces
                      </button>
                    ) : (
                      <button onClick={cerrar} className="px-4 py-2 border text-sm rounded">Cerrar</button>
                    )}
                  </div>
                </>
              )}
            </div>
          </div>
        </BodyPortal>
      )}
    </>
  );
}
